import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Icon from '../../../components/AppIcon';

const Footer = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const currentYear = new Date()?.getFullYear();

  const footerLinks = {
    platform: [
      { label: t('header.playerProfiles'), path: '/player-profiles' },
      { label: t('header.services'), path: '/services-overview' },
      { label: t('header.partnershipNetwork'), path: '/partnership-network' },
      { label: t('header.successStories'), path: '/success-stories' }
    ],
    resources: [
      { label: t('header.marketIntelligence'), path: '/market-intelligence' },
      { label: t('header.complianceCenter'), path: '/compliance-center' },
      { label: t('header.clientPortal'), path: '/client-portal' },
      { label: t('header.contact'), path: '/contact' }
    ],
    company: [
      { label: t('homepage.footer.aboutUs'), path: '/homepage' },
      { label: t('homepage.footer.careers'), path: '/homepage' },
      { label: t('homepage.footer.press'), path: '/homepage' },
      { label: t('homepage.footer.blog'), path: '/homepage' }
    ],
    legal: [
      { label: t('homepage.footer.privacyPolicy'), path: '/homepage' },
      { label: t('homepage.footer.termsOfService'), path: '/homepage' },
      { label: t('homepage.footer.cookiePolicy'), path: '/homepage' },
      { label: t('homepage.footer.gdprCompliance'), path: '/homepage' }
    ]
  };

  const socialLinks = [
    { icon: 'Facebook', url: 'https://facebook.com' },
    { icon: 'Twitter', url: 'https://twitter.com' },
    { icon: 'Instagram', url: 'https://instagram.com' },
    { icon: 'Linkedin', url: 'https://linkedin.com' },
    { icon: 'Youtube', url: 'https://youtube.com' }
  ];

  return (
    <footer className="bg-card border-t border-border">
      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-12 md:py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-8 md:gap-12 mb-12">
          <div className="lg:col-span-2">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                <Icon name="Trophy" size={24} color="var(--color-primary)" />
              </div>
              <span className="text-xl font-bold text-primary">ProX11Market</span>
            </div>
            <p className="text-sm text-muted-foreground mb-6">
              {t('homepage.footer.description')}
            </p>
            <div className="flex items-center gap-3">
              {socialLinks?.map((social, index) => (
                <a
                  key={index}
                  href={social?.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-lg bg-muted hover:bg-primary/10 flex items-center justify-center transition-colors duration-300"
                >
                  <Icon name={social?.icon} size={18} color="var(--color-foreground)" />
                </a>
              ))}
            </div>
          </div>

          <div>
            <h3 className="text-sm font-semibold text-foreground mb-4">{t('homepage.footer.platform')}</h3>
            <ul className="space-y-3">
              {footerLinks?.platform?.map((link, index) => (
                <li key={index}>
                  <button
                    onClick={() => navigate(link?.path)}
                    className="text-sm text-muted-foreground hover:text-primary transition-colors duration-300"
                  >
                    {link?.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="text-sm font-semibold text-foreground mb-4">{t('homepage.footer.resources')}</h3>
            <ul className="space-y-3">
              {footerLinks?.resources?.map((link, index) => (
                <li key={index}>
                  <button
                    onClick={() => navigate(link?.path)}
                    className="text-sm text-muted-foreground hover:text-primary transition-colors duration-300"
                  >
                    {link?.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="text-sm font-semibold text-foreground mb-4">{t('homepage.footer.company')}</h3>
            <ul className="space-y-3">
              {footerLinks?.company?.map((link, index) => (
                <li key={index}>
                  <button
                    onClick={() => navigate(link?.path)}
                    className="text-sm text-muted-foreground hover:text-primary transition-colors duration-300"
                  >
                    {link?.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="text-sm font-semibold text-foreground mb-4">{t('homepage.footer.legal')}</h3>
            <ul className="space-y-3">
              {footerLinks?.legal?.map((link, index) => (
                <li key={index}>
                  <button
                    onClick={() => navigate(link?.path)}
                    className="text-sm text-muted-foreground hover:text-primary transition-colors duration-300"
                  >
                    {link?.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="pt-8 border-t border-border">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-sm text-muted-foreground text-center md:text-left">
              © {currentYear} ProX11Market. {t('homepage.footer.allRightsReserved')}. FIFA Licensed Intermediary.
            </p>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <Icon name="Shield" size={16} color="var(--color-success)" />
                <span className="text-xs text-muted-foreground">FIFA Certified</span>
              </div>
              <div className="flex items-center gap-2">
                <Icon name="Award" size={16} color="var(--color-success)" />
                <span className="text-xs text-muted-foreground">CAF Approved</span>
              </div>
              <div className="flex items-center gap-2">
                <Icon name="CheckCircle" size={16} color="var(--color-success)" />
                <span className="text-xs text-muted-foreground">FA Registered</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;